import React, { useState, useRef, useEffect } from "react";
import { Bell, X, Heart, MessageCircle, UserPlus, Star, Mail, Check, Settings } from "lucide-react";
import { notifications as initialNotifications } from "../data/mockData";
import { useLayout } from "../context/LayoutContext";

const NotificationCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState(initialNotifications);
  const { layoutMode } = useLayout();
  const dropdownRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  const iconMap = {
    Heart,
    MessageCircle,
    UserPlus,
    Star,
    Mail
  };

  const markAsRead = (id) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Prevent scrolling when mobile notification sheet is open
      if (layoutMode === "mobile") {
        document.body.style.overflow = "hidden";
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, layoutMode]);

  const NotificationList = () => (
    <div className="flex-1 overflow-y-auto custom-scrollbar">
      {notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 p-12">
          <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-4">
             <Bell size={40} className="text-slate-200" />
          </div>
          <p className="text-sm font-bold text-slate-400">No new notifications</p>
          <p className="text-xs text-slate-300 mt-1">We'll alert you when something happens!</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-50">
          {notifications.map((notif) => {
            const Icon = iconMap[notif.icon] || Bell;
            const iconBg = {
              'like': 'bg-red-50 text-red-500',
              'comment': 'bg-blue-50 text-blue-500',
              'follow': 'bg-green-50 text-green-500',
              'subscription': 'bg-purple-50 text-purple-500',
              'message': 'bg-amber-50 text-amber-500'
            }[notif.type] || 'bg-slate-50 text-slate-400';

            return (
              <div
                key={notif.id}
                onClick={() => markAsRead(notif.id)}
                className={`px-4 py-4 flex items-start gap-4 hover:bg-slate-50 cursor-pointer transition-all relative ${
                  !notif.read ? 'bg-blue-50/20' : ''
                }`}
              >
                {!notif.read && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r-full"></div>
                )}
                
                {/* Avatar with Type Icon Badge */}
                <div className="relative shrink-0">
                  <img
                    src={notif.avatarUrl}
                    alt=""
                    className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                  />
                  <div className={`absolute -bottom-1 -right-1 p-1 rounded-full border-2 border-white shadow-sm ${iconBg}`}>
                    <Icon size={12} strokeWidth={3} />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pt-0.5">
                  <p className={`text-sm leading-snug ${!notif.read ? 'font-bold text-slate-900' : 'text-slate-600 font-medium'}`}>
                    {notif.message}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-1.5 font-bold uppercase tracking-wider">{notif.timestamp}</p>
                </div>

                {/* Mark as read button (subtle) */}
                {!notif.read && (
                  <button className="shrink-0 text-primary w-6 h-6 flex items-center justify-center hover:bg-primary/10 rounded-full transition-colors">
                     <Check size={14} strokeWidth={3} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="relative" ref={layoutMode === "wide" ? dropdownRef : null}>
      {/* Bell Icon with Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 text-slate-700 hover:text-primary hover:bg-slate-50 rounded-2xl transition-all active:scale-90 shadow-sm bg-white"
      >
        <Bell size={22} strokeWidth={2.5} />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center border-2 border-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* MOBILE BOTTOM SHEET */}
      {layoutMode === "mobile" && (
        <>
          {/* Backdrop */}
          <div 
            className={`fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            onClick={() => setIsOpen(false)}
          ></div>
          
          {/* Sheet */}
          <div 
            className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-[40px] z-[101] shadow-2xl transition-transform duration-500 ease-out flex flex-col ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}
            style={{ height: '85vh' }}
          >
            {/* Handle */}
            <div className="w-16 h-1.5 bg-slate-200 rounded-full mx-auto mt-4 mb-2"></div>
            
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between border-b border-slate-50">
               <div>
                  <h3 className="text-2xl font-black text-slate-900 tracking-tight">Notifications</h3>
                  <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-0.5">Stay Updated</p>
               </div>
               <div className="flex items-center gap-3">
                  {unreadCount > 0 && (
                    <button 
                      onClick={markAllAsRead}
                      className="p-2.5 bg-primary/10 text-primary rounded-xl font-bold text-xs"
                    >
                      Clear All
                    </button>
                  )}
                  <button 
                    onClick={() => setIsOpen(false)}
                    className="p-2.5 bg-slate-100 text-slate-500 rounded-xl"
                  >
                    <X size={20} />
                  </button>
               </div>
            </div>

            <NotificationList />

            {/* Footer */}
            <div className="p-6 border-t border-slate-50">
               <button className="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold shadow-lg shadow-slate-200 active:scale-95 transition-all">
                  Load Older Activity
               </button>
            </div>
          </div>
        </>
      )}

      {/* DESKTOP DROPDOWN */}
      {layoutMode === "wide" && isOpen && (
        <div className="absolute right-0 mt-3 w-96 bg-white rounded-3xl shadow-2xl border border-slate-100 z-50 flex flex-col overflow-hidden max-h-[600px] animate-in fade-in zoom-in duration-200 origin-top-right">
          <div className="p-5 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
            <h3 className="text-lg font-black text-slate-900">Notifications</h3>
            <div className="flex items-center gap-2">
              <button 
                onClick={markAllAsRead}
                className="text-[10px] font-black uppercase tracking-widest text-primary hover:text-primary-dark p-2"
              >
                Mark all read
              </button>
              <button className="p-2 text-slate-400 hover:text-slate-600">
                <Settings size={16} />
              </button>
            </div>
          </div>
          <NotificationList />
          <div className="p-4 bg-slate-50/50 text-center border-t border-slate-50">
            <button className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-primary transition-colors">
              View All History
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
